import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import FundraiserHeader from "../common/FundraiserHeader";
import "./fundraiserbrand.scss";
import { useDispatch, useSelector } from "react-redux";
import { getFundraiserContent } from "../../../services/fundraiserService";
import Loader from "../../common/Loader";
import { useNavigate, useParams } from "react-router-dom";
import { getS3PublicUrl } from "../../../utils/s3";

const FundraiserBrandDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.fundraiserReducer?.loading);
  const id = useParams();
  const [content, setContent] = useState();

  const getContent = async () => {
    const getPromise = await dispatch(
      getFundraiserContent({ userId: id?.id }),
    )?.unwrap();
    if (getPromise?.success === true) {
      setContent(getPromise?.data);
    }
  };

  useEffect(() => {
    getContent();
  }, []);

  const handleJoinClick = () => {
    // New flow entry: go straight to new member setup with fundraiser context
    navigate(`/member-setup?fundraiserId=${content?.user?._id || id?.id}`);
  };
  const fundraiserName = content?.user?.name || "Fundraiser";
  const logoUrl = content?.logo ? getS3PublicUrl(content?.logo) : "";
  const bannerUrl = content?.banner ? getS3PublicUrl(content?.banner) : "";
  const websiteUrl = content?.website || "";
  const normalizedWebsiteHref = websiteUrl
    ? /^(https?:)?\/\//i.test(websiteUrl)
      ? websiteUrl
      : `https://${websiteUrl}`
    : "";

  return (
    <div className="fundraiser-brand-page w-full min-h-screen">
      <Helmet>
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content={`Support ${fundraiserName} on Swoodle`}
        />
        <meta
          property="og:description"
          content={`Join us in supporting ${fundraiserName}. Get amazing discounts while contributing to a great cause!`}
        />
        {content?.banner ? (
          <meta property="og:image" content={bannerUrl} />
        ) : null}

        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={`Support ${fundraiserName} on Swoodle`}
        />
        <meta
          name="twitter:description"
          content={`Join us in supporting ${fundraiserName}. Get amazing discounts while contributing to a great cause!`}
        />
        {content?.banner ? (
          <meta name="twitter:image" content={bannerUrl} />
        ) : null}
      </Helmet>

      {loading === true && <Loader />}
      <FundraiserHeader brandLogo={null} />
      <div className="fundraiser-brand-shell">
        <div className="fundraiser-brand-card">
          <div className="fundraiser-brand-card__texture" />

          <section className="fundraiser-brand-hero">
            <div className="fundraiser-brand-hero__title-row">
              <div className="fundraiser-brand-hero__title-block">
                <h1 className="fundraiser-brand-hero__title">
                  {fundraiserName}
                </h1>
                <div className="fundraiser-brand-hero__accent" />
              </div>
              <button
                className="fundraiser-brand-hero__button"
                onClick={handleJoinClick}
              >
                <span>Join Now</span>
                <span
                  className="fundraiser-brand-hero__button-arrow"
                  aria-hidden="true"
                >
                  <svg viewBox="0 0 24 24" fill="none">
                    <path
                      d="M5 12H19M13 6L19 12L13 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </button>
            </div>

            {logoUrl ? (
              <div className="fundraiser-brand-hero__logo-wrap">
                <img
                  src={logoUrl}
                  alt="Fundraiser Logo"
                  className="fundraiser-brand-hero__logo"
                />
              </div>
            ) : null}

            <div className="fundraiser-brand-hero__content">
              <div className="fundraiser-brand-hero__intro">
                <p className="fundraiser-brand-hero__lead">
                  We are excited to invite you to join us in fundraising for
                  the{" "}
                  <span className="fundraiser-brand-hero__highlight">
                    {fundraiserName}!
                  </span>
                </p>

                {websiteUrl ? (
                  <a
                    href={normalizedWebsiteHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="fundraiser-brand-hero__website"
                  >
                    <span
                      className="fundraiser-brand-hero__website-icon"
                      aria-hidden="true"
                    >
                      <svg viewBox="0 0 24 24" fill="none">
                        <path
                          d="M12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2Z"
                          stroke="currentColor"
                          strokeWidth="1.8"
                        />
                        <path
                          d="M2.5 12H21.5M12 2.5C14.4 5.2 15.764 8.553 15.85 12C15.764 15.447 14.4 18.8 12 21.5M12 2.5C9.6 5.2 8.236 8.553 8.15 12C8.236 15.447 9.6 18.8 12 21.5"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                        />
                      </svg>
                    </span>
                    <span>{websiteUrl}</span>
                  </a>
                ) : null}
              </div>
            </div>
          </section>

          <div className="fundraiser-brand-divider" />

          <section className="fundraiser-brand-section">
            <div
              className="rich-text-content fundraiser-brand-copy"
              dangerouslySetInnerHTML={{ __html: content?.aboutUs || "" }}
            />
          </section>

          <section className="fundraiser-brand-section fundraiser-brand-section--support">
            <div className="fundraiser-brand-section__heading-row">
              <span
                className="fundraiser-brand-section__heading-icon"
                aria-hidden="true"
              >
                <svg viewBox="0 0 24 24" fill="none">
                  <path
                    d="M9 11C10.933 11 12.5 9.433 12.5 7.5C12.5 5.567 10.933 4 9 4C7.067 4 5.5 5.567 5.5 7.5C5.5 9.433 7.067 11 9 11Z"
                    fill="currentColor"
                  />
                  <path
                    d="M17 10C18.381 10 19.5 8.881 19.5 7.5C19.5 6.119 18.381 5 17 5C15.619 5 14.5 6.119 14.5 7.5C14.5 8.881 15.619 10 17 10Z"
                    fill="currentColor"
                    opacity="0.7"
                  />
                  <path
                    d="M3.5 18.5C3.5 15.739 5.739 13.5 8.5 13.5H9.5C12.261 13.5 14.5 15.739 14.5 18.5V19H3.5V18.5Z"
                    fill="currentColor"
                  />
                  <path
                    d="M14.5 18C14.5 15.791 16.291 14 18.5 14C20.709 14 22.5 15.791 22.5 18V19H14.5V18Z"
                    fill="currentColor"
                    opacity="0.7"
                  />
                </svg>
              </span>
              <h2 className="fundraiser-brand-section__title">
                Why We Need Your Support
              </h2>
            </div>
            <div className="fundraiser-brand-section__accent" />
            <div
              className="rich-text-content fundraiser-brand-copy"
              dangerouslySetInnerHTML={{ __html: content?.support || "" }}
            />
          </section>

          <section className="fundraiser-brand-signoff">
            <p className="fundraiser-brand-signoff__text">
              We look forward to working with you.
            </p>
            {/* {logoUrl ? (
              <div className="fundraiser-brand-signoff__identity">
                <img
                  src={logoUrl}
                  alt="Fundraiser Logo"
                  className="fundraiser-brand-signoff__logo"
                />
                <span className="fundraiser-brand-signoff__name">
                  {fundraiserName} Team
                </span>
              </div>
            ) : null} */}
          </section>

          {bannerUrl ? (
            <div className="fundraiser-brand-banner">
              <img
                src={bannerUrl}
                alt="Fundraiser Banner"
                className="fundraiser-brand-banner__image"
              />
            </div>
          ) : null}

          <section className="fundraiser-brand-cta">
            <div className="fundraiser-brand-cta__badge" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 21C7.029 18.11 4 14.87 4 10.91C4 8.25 6.068 6 8.765 6C10.31 6 11.562 6.777 12 7.852C12.438 6.777 13.69 6 15.235 6C17.932 6 20 8.25 20 10.91C20 14.87 16.971 18.11 12 21Z"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="fundraiser-brand-cta__copy">
              <h3 className="fundraiser-brand-cta__title">
                Your support makes a real difference.
              </h3>
              <p className="fundraiser-brand-cta__subtitle">
                Join us today and help {fundraiserName} achieve their goals.
              </p>
            </div>
            <button
              className="fundraiser-brand-cta__button"
              onClick={handleJoinClick}
            >
              <span>Join Now</span>
              <span
                className="fundraiser-brand-cta__button-arrow"
                aria-hidden="true"
              >
                <svg viewBox="0 0 24 24" fill="none">
                  <path
                    d="M5 12H19M13 6L19 12L13 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </button>
          </section>
        </div>
      </div>
    </div>
  );
};

export default FundraiserBrandDetails;
